import type { TNamedTargetSpeciality, TSpecialityChip } from '../types/targetSpeciality';
import type { TPhysician } from '../types/user';
import { uniqueById } from './common';

/**
 * A person is "actively training" when any of their `physician` records has a target
 * speciality set. `physician` is an o2m list, so this checks all of them rather than
 * assuming a single record.
 */
export const hasActiveTraining = (
  user: { physician?: ReadonlyArray<Pick<TPhysician, 'target_speciality'> | null> | null } | null | undefined,
): boolean => (user?.physician ?? []).some((physician) => physician?.target_speciality != null);

const NAMED_TARGET_SPECIALITY_FIELDS = ['id', 'parent_speciality.name', 'child_speciality.name'];

/** A `physician` row's training goal and completed titles, each with both parts' names. */
export const PHYSICIAN_SPECIALITY_FIELDS: ReadonlyArray<string> = [
  ...NAMED_TARGET_SPECIALITY_FIELDS.map((field) => `target_speciality.${field}`),
  ...NAMED_TARGET_SPECIALITY_FIELDS.map(
    (field) => `completed_target_specialities.target_speciality_id.${field}`,
  ),
];

type TChipPhysician = Readonly<{
  target_speciality?: TNamedTargetSpeciality | null
  completed_target_specialities?: ReadonlyArray<{
    target_speciality_id: TNamedTargetSpeciality | null
  }> | null
}>;

const toChips = (
  specialities: ReadonlyArray<TNamedTargetSpeciality | null | undefined>,
  training: boolean,
): TSpecialityChip[] => specialities.flatMap((speciality) => {
  const name = speciality?.child_speciality?.name || speciality?.parent_speciality?.name;
  return speciality && name ? [{
    id: speciality.id, name, training,
  }] : [];
});

/**
 * The completed Facharzt titles, then the training goal, named by their most specific part.
 * A speciality held as both shows once, as the training goal.
 */
export const specialityChipsOf = (
  physicians: ReadonlyArray<TChipPhysician | null | undefined>,
): TSpecialityChip[] => {
  const goals = uniqueById(
    toChips(physicians.map((physician) => physician?.target_speciality), true),
  );
  const goalIds = new Set(goals.map(({ id }) => id));
  const completedSpecialities = physicians.flatMap(
    (physician) => physician?.completed_target_specialities ?? [],
  ).map((relation) => relation.target_speciality_id);
  const completed = toChips(completedSpecialities, false);
  return [...uniqueById(completed.filter(({ id }) => !goalIds.has(id))), ...goals];
};
